import { createContext, useContext, useMemo, type FC, type PropsWithChildren } from 'react';
import { type Agent, getNodes, useListAllAgentsQuery } from '@matrix-forge/graphql-client';
import { type ApolloError } from '@apollo/client';
import { UserInfoContext } from './UserInfoContext';
import { DEVO_ASSISTANT_NAME, WORKFLOW_AGENT_NAME } from '@/constants/agents';

export type AgentsContextValue = {
  agents: Agent[] | null;
  loading: boolean;
  error: ApolloError | null;
  refetch: () => Promise<any>;
};

export const AgentsContext = createContext<AgentsContextValue | undefined>(undefined);

export const AgentsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data, previousData, loading, error, refetch } = useListAllAgentsQuery();
  const { userInfo } = useContext(UserInfoContext);

  // Optimization: to make UX less jarring, if we are refetching and we don't have
  // any data but we do have the previous data, use the previous data until newer data arrives.
  const dataResolved = !data && loading && previousData ? previousData : data;
  const value = useMemo(() => {
    // Wait for userInfo to load before applying feature flag filters
    // This prevents race conditions and flickering
    if (!userInfo && !loading) {
      return {
        agents: null,
        loading: true,
        error: null,
        refetch,
      };
    }
    const combinedEdges =
      dataResolved?.templateAgents?.edges && dataResolved?.customAgents?.edges
        ? [...dataResolved.templateAgents.edges, ...dataResolved.customAgents.edges]
        : dataResolved?.templateAgents?.edges || dataResolved?.customAgents?.edges;
    let agents = getNodes(combinedEdges) || null;

    // Sort agents by display_order (nulls last), then alphabetically by name.
    if (agents) {
      agents.sort((a1, a2) => {
        const order1 = a1.displayOrder ?? Number.MAX_SAFE_INTEGER;
        const order2 = a2.displayOrder ?? Number.MAX_SAFE_INTEGER;
        if (order1 !== order2) return order1 - order2;
        return a1.name.localeCompare(a2.name);
      });

      // Filter out Devo Assistant if feature flag is disabled
      // This ensures it's not accessible anywhere in the UI, including direct URL access
      if (userInfo?.features?.devoAssistantEnabled === false) {
        agents = agents.filter(agent => agent.name !== DEVO_ASSISTANT_NAME);
      }

      // Filter out Workflow Agent if feature flag is disabled
      if (userInfo?.features?.workflowAgentEnabled === false) {
        agents = agents.filter(agent => agent.name !== WORKFLOW_AGENT_NAME);
      }

      // Filter out hidden agents from user-facing navigation
      agents = agents.filter(agent => !agent.isHidden);
    }

    return {
      agents,
      loading: !!loading,
      error: error || null,
      refetch,
    };
  }, [dataResolved, loading, error, refetch, userInfo?.features?.devoAssistantEnabled, userInfo?.features?.workflowAgentEnabled, userInfo]);
  return <AgentsContext value={value as any}>{children}</AgentsContext>;
};
